import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getMostRecentLocations } from 'emergency_data/src/utils/getMostRecentLoc';
import { CopyIcon } from '@rsos/assets/icons';
import { gspx } from '@rsos/capstone';
import { INTERNATIONAL_SUPPORT } from '@rsos/capstone/src/constants/capabilities';
import { Text } from '@rsos/components/capstone/base';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import { formatPhoneWithDashes } from '@rsos/utils';
import copyToClipboard from '@rsos/utils/copyToClipboard';
import { convertUnixTimestamp } from '@rsos/utils/timeHelpers';
import { WHAT_3_WORDS } from '../../constants/permissions';
import { isExtraInfoOrMeta } from '../JurisdictionView/helpers';
import { formatValue, getDataSource } from '../Location/helpers';
import { CopyButton } from './CopyAllButton.styles';
import { handleClipboardData } from './copyAllHelpers';

export const CopyAllButton = () => {
  const permissions = useSelector(state =>
    selectUserPermissionsByApp(state.sinatra.user, 'capstone'),
  );

  const hasWhat3wordsAccess = permissions?.includes(WHAT_3_WORDS);

  const isInternational = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP.activeCapabilities &&
        state.psaps.currentPSAP.activeCapabilities[INTERNATIONAL_SUPPORT]
      ),
  );

  const { selectedCallerID, callQueue } = useSelector(
    state => state.emergencyCalls,
  );

  const {
    mostRecentLocation,
    mostRecentSecondaryLocation,
  } = getMostRecentLocations(callQueue[selectedCallerID]);

  const location = mostRecentLocation || mostRecentSecondaryLocation;

  const what3words = useSelector(
    state =>
      state.what3words.calls[selectedCallerID]?.[
        `${location.latitude}_${location.longitude}`
      ]?.words,
  );

  const { irpVersion } = useSelector(state => state.sinatra.irp);

  const {
    altitude = null,
    altitude_uncertainty_radius = null,
    caller_id = null,
    latitude = null,
    location_time = null,
    longitude = null,
    rg_address = null,
    rg_exact_location = null,
    rg_floor = null,
    rgAddress = null,
    rgAddressProvider = null,
    timestamp = null,
    uncertainty_confidence = null,
    uncertainty_radius = null,
  } = location;

  const locationData = {
    callerID: isInternational ? caller_id : formatPhoneWithDashes(caller_id),
    source: getDataSource(location),
    locationTime: convertUnixTimestamp(
      timestamp ? parseFloat(timestamp) : parseFloat(location_time),
    ),
    estimatedAddress: rg_address || rgAddress,
    estimatedAddressProvider: rgAddressProvider,
    estimatedFloor: rg_exact_location || rg_floor,
    latitude: formatValue(latitude),
    longitude: formatValue(longitude),
    what3words: hasWhat3wordsAccess
      ? `https://what3words.com/${what3words}`
      : null,
    altitude: altitude ? `${parseFloat(altitude).toFixed(1)} meters` : null,
    altitudeUncertaintyRadius: altitude_uncertainty_radius
      ? `${Number(altitude_uncertainty_radius).toFixed(0)} meters`
      : null,
    uncertaintyRadius: `${Number(uncertainty_radius).toFixed(1)} meters`,
    uncertaintyConfidence: uncertainty_confidence
      ? `${uncertainty_confidence} %`
      : null,
  };

  const [isActive, setIsActive] = useState(false);

  const handleReset = () => {
    setIsActive(false);
  };

  const handleClick = () => {
    const { payloadID, requestID } = isExtraInfoOrMeta(mostRecentLocation);
    gspx.trackCustomEvent('Data Location', {
      irpVersion,
      name: `Copy Lat and Long`,
      Category: 'Location Card Copy coordinates',
      'Launched date': new Date(),
      payloadID,
      requestID,
    });

    copyToClipboard(handleClipboardData(locationData));

    setIsActive(true);

    setTimeout(() => {
      handleReset();
    }, 3000);
  };

  useEffect(() => {
    handleReset();
  }, [selectedCallerID]);

  return (
    <CopyButton isActive={isActive} onClick={handleClick}>
      <CopyIcon />
      <Text ml={'5px'} size="xsmall">
        {isActive ? 'Copied' : 'Copy All'}
      </Text>
    </CopyButton>
  );
};

export default CopyAllButton;
